import React from "react";
import { find, get, startCase, toLower } from "lodash";
import { formatValue } from "utils/number";
import { accountTypesToEnglishMapping } from "statics/accountTypes";
import "./TransferDetails.scss";

interface TransferMetadata {
  label?: string;
  // required: used as the lodash `get` path and the React key when ordering
  key: string;
  // metadata entries also carry a `format` consumed by formatValue
  format?: string | ((data: unknown) => unknown);
}

interface TransferHeaderConfig {
  showTiming?: boolean;
  timingText?: string;
  amountKey?: string;
  fromKey?: string;
  toKey?: string;
  authorizationText?: string;
}

interface TransferDetailsProps {
  metadata: TransferMetadata[];
  data?: Record<string, unknown>;
  title?: string;
  showHeader?: boolean;
  showAuthorization?: boolean;
  headerConfig?: TransferHeaderConfig;
}

const accountType = (accountType: string) => {
  return (
    accountTypesToEnglishMapping[
      accountType as keyof typeof accountTypesToEnglishMapping
    ] || accountType
  );
};

const TransferDetails = ({
  data,
  metadata,
  title,
  showHeader = false,
  showAuthorization = false,
  headerConfig = {},
}: TransferDetailsProps) => {
  const {
    amountKey = "amount",
    fromKey = "fromAccount.bankAlias",
    toKey = "toAccount.accountType",
    showTiming,
    timingText,
    authorizationText,
  } = headerConfig;

  const orderedKeys = [
    "fromAccount.bankAlias",
    "transferDate",
    "toAccount.accountType",
    "frequency",
    "taxYear",
  ];

  const orderedMetadata = orderedKeys
    .map((key) => metadata.find((meta) => meta.key === key))
    .filter((meta): meta is TransferMetadata => Boolean(meta));

  // Use normalized keys directly
  const amountMeta = find(metadata, { key: amountKey });
  const amount = showHeader && get(data, amountKey);
  const fromAccount = get(data, fromKey);
  const toAccount = get(data, toKey);

  return (
    <div className="transfer-details">
      {title && <p className="transfer-title">{title}</p>}

      {showHeader && (
        <>
          {fromAccount && toAccount && (
            <div className="route">
              Transfer from {fromAccount as React.ReactNode} to{" "}
              {accountType(toAccount as string)}
            </div>
          )}

          {amount && (
            <div className="amount">
              {amount && (
                <div className="amount">
                  {formatValue(amountMeta!, amount) as React.ReactNode}
                </div>
              )}
            </div>
          )}

          {showTiming && (
            <div className="timing">
              {timingText || "Funds should arrive in 5 business days."}
            </div>
          )}
        </>
      )}

      {orderedMetadata.map((meta) => {
        const borderedKeys = ["fromAccount.bankAlias", "toAccount.accountType"];
        const extraClass = borderedKeys.includes(meta.key) ? "with-border" : "";

        return (
          <div className={`detail-item ${extraClass}`} key={meta.key}>
            <span className="label">{startCase(toLower(meta.label))}</span>
            <span className="value">
              {formatValue(meta, get(data, meta.key)) as React.ReactNode}
            </span>
          </div>
        );
      })}

      {showAuthorization && (
        <div className="authorization">
          {authorizationText ||
            `By clicking submit, I authorize Icon Financial Services to initiate the 
             transaction details above. Scheduled payments may be canceled up to 1 
             day in advance.`}
        </div>
      )}
    </div>
  );
};

export default TransferDetails;
